// Home Field: a dot on a map of the US, and four schools. Tap the one that
// plays there. Ten stadiums a day, and every one is played, right or wrong.
//
// Of the three wrong answers, one is a near neighbour of the right one and
// two are a few hundred miles off, in the same part of the country. A dot in
// Iowa City might offer Iowa State, then Kansas State and Wisconsin: one call takes
// knowing the state lines, the others only the region. Three neighbours made
// every question a coin toss between dots a thumb's width apart.
//
// Stadiums closer than MIN_MILES apart (UCLA and USC, Miami and FIU) are
// never offered together, because no one could tell those dots apart.
//
// Stadium positions come from data/stadiums.js, generated once from CFBD by
// scripts/build-game-map.js.
import STADIUMS from "./data/stadiums";
import { MAP_HEIGHT, MAP_WIDTH } from "./data/usMap";
import { seededRandom, shuffle, squares } from "./daily";

export const GAME_ID = "home-field";
export const GAME_NAME = "Home Field";
export const QUESTIONS = 10;

const MIN_MILES = 40;
// The near wrong answer comes from this many nearest stadiums, so the same
// neighbour does not turn up every time a school does.
const NEAREST = 6;
// The other two sit in this band from the right answer, and at least
// APART_MILES from every other option, so their dots read as separate places.
const FAR_MIN_MILES = 180;
const FAR_MAX_MILES = 600;
const APART_MILES = 120;
// The practice question uses far-apart schools, so it shows how the game
// works rather than testing anyone.
const PRACTICE_MIN_MILES = 700;

const EARTH_MILES = 3958.8;
const rad = (deg) => (deg * Math.PI) / 180;

export function milesBetween(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_MILES * Math.asin(Math.sqrt(h));
}

const COMPASS = ["north", "northeast", "east", "southeast", "south", "southwest", "west", "northwest"];

/** "west": which way `b` lies from `a`, to the nearest of eight points. */
export function directionTo(a, b) {
  const y = Math.sin(rad(b.lon - a.lon)) * Math.cos(rad(b.lat));
  const x = Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) - Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(b.lon - a.lon));
  const bearing = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  return COMPASS[Math.round(bearing / 45) % 8];
}

/** Stadiums for the FBS teams the app knows, sorted by slug. */
function playable(teams) {
  return Object.keys(STADIUMS).filter((slug) => teams?.[slug]?.classification === "fbs").sort();
}

// Adds up to `count` of `candidates` to `chosen`, in the order given, each
// at least `apart` miles from everything already chosen.
function take(chosen, candidates, count, apart) {
  const added = [];
  for (const slug of candidates) {
    if (added.length === count) break;
    if ([...chosen, ...added].every((c) => milesBetween(STADIUMS[c], STADIUMS[slug]) >= apart)) added.push(slug);
  }
  return added;
}

function byDistanceFrom(answer, slugs) {
  const here = STADIUMS[answer];
  return slugs
    .filter((slug) => slug !== answer)
    .map((slug) => ({ slug, miles: milesBetween(here, STADIUMS[slug]) }))
    .sort((a, b) => a.miles - b.miles || a.slug.localeCompare(b.slug));
}

function question(answer, slugs, random) {
  const all = byDistanceFrom(answer, slugs);
  const near = take([answer], shuffle(all.filter((o) => o.miles >= MIN_MILES).slice(0, NEAREST).map((o) => o.slug), random), 1, MIN_MILES);
  const band = shuffle(all.filter((o) => o.miles >= FAR_MIN_MILES && o.miles <= FAR_MAX_MILES).map((o) => o.slug), random);
  let far = take([answer, ...near], band, 2, APART_MILES);
  // A school with few others in range (Hawaii, the far northwest) falls back
  // to the nearest beyond the band's inner edge.
  if (far.length < 2) {
    const beyond = all.filter((o) => o.miles >= FAR_MIN_MILES).map((o) => o.slug);
    far = [...far, ...take([answer, ...near, ...far], beyond, 2 - far.length, APART_MILES)];
  }
  return { answer, options: shuffle([answer, ...near, ...far], random) };
}

/** The day's questions. The same on every device for the same day. */
export function buildQuestions(teams, day) {
  const slugs = playable(teams);
  if (slugs.length < QUESTIONS + 3) return null;
  const random = seededRandom(`${GAME_ID}:${day}`);
  return shuffle(slugs, random)
    .slice(0, QUESTIONS)
    .map((answer) => question(answer, slugs, random));
}

/** The one practice question, shown the first time someone plays. */
export function buildPractice(teams) {
  const slugs = playable(teams);
  if (slugs.length < 4) return null;
  const random = seededRandom(`${GAME_ID}:practice`);
  // A school most people can place.
  const answer = ["texas", "florida", "ohio-state", "oregon", "alabama"].find((s) => slugs.includes(s)) || slugs[0];
  const far = byDistanceFrom(answer, slugs).filter((o) => o.miles >= PRACTICE_MIN_MILES).map((o) => o.slug);
  const wrong = take([answer], shuffle(far, random), 3, PRACTICE_MIN_MILES);
  return { answer, options: shuffle([answer, ...wrong], random) };
}

export const stadiumFor = (slug) => STADIUMS[slug] || null;

/**
 * The map box to show the answer in: the four schools with room around them,
 * at the map's own shape, so the dots spread out and the state lines between
 * them read. Never tighter than about a state and a half across.
 */
export function answerViewBox(slugs) {
  const pts = slugs.map((s) => STADIUMS[s]).filter(Boolean);
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const pad = 40;
  const ratio = MAP_WIDTH / MAP_HEIGHT;
  let w = Math.max(Math.max(...xs) - Math.min(...xs) + pad * 2, 200);
  let h = Math.max(Math.max(...ys) - Math.min(...ys) + pad * 2, 200 / ratio);
  if (w / h > ratio) h = w / ratio;
  else w = h * ratio;
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return [cx - w / 2, cy - h / 2, w, h].map((n) => Math.round(n * 10) / 10);
}

/** The line under the answer: where it is, and where the wrong pick was. */
export function answerNote(answer, picked, teams) {
  const here = STADIUMS[answer];
  const capacity = here.capacity ? ` Holds ${here.capacity.toLocaleString("en-US")}.` : "";
  const place = `${here.stadium}, ${here.city}, ${here.state}.${capacity}`;
  if (!picked || picked === answer || !STADIUMS[picked]) return { place, miss: null };
  const there = STADIUMS[picked];
  const miles = Math.round(milesBetween(here, there) / 10) * 10;
  const name = teams?.[picked]?.name || picked;
  return { place, miss: `${name} plays ${miles.toLocaleString("en-US")} miles ${directionTo(here, there)}, in ${there.city}.` };
}

export function shareText(answers, dayLabel) {
  const score = answers.filter(Boolean).length;
  const trophy = score === QUESTIONS ? " 🏆" : "";
  return `Lineup CFB ${GAME_NAME} · ${dayLabel}\n${squares(answers)} ${score}/${QUESTIONS}${trophy}`;
}
